const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Helper function to handle API responses
const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  return response.json();
};

// API service class
class ApiService {
  // Generic request method
  private async request(endpoint: string, options: RequestInit = {}) {
    const token = getAuthToken();
    const url = `${API_BASE_URL}${endpoint}`;
    
    console.log('API Service: Making request to:', url);
    console.log('API Service: Request options:', options);
    console.log('API Service: Token exists:', !!token);
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    console.log('API Service: Request config:', config);
    
    const response = await fetch(url, config);
    console.log('API Service: Response status:', response.status);
    console.log('API Service: Response headers:', response.headers);
    
    if (response.status === 401) {
      // Clear invalid token
      localStorage.removeItem('token');
      throw new Error('Authentication required. Please log in again.');
    }
    
    return handleResponse(response);
  }

  // Authentication
  async login(email: string, password: string) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async validateToken(token: string) {
    const url = `${API_BASE_URL}/users/validate-token/${token}`;
    console.log('API Service: Validating token at:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    return response;
  }

  // Dashboard Statistics
  async getDashboardStats() {
    return this.request('/dashboard/stats');
  }

  async getProjectStats() {
    return this.request('/dashboard/project-stats');
  }

  async getFundRequestStats() {
    return this.request('/dashboard/fund-request-stats');
  }

  async getRecentProjects(limit = 5) {
    const url = `${API_BASE_URL}/projects?limit=${limit}&sortBy=createdAt&sortOrder=desc`;
    console.log('API Service: Getting recent projects from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  async getRecentFundRequests(limit = 5) {
    const url = `${API_BASE_URL}/fund-requests?limit=${limit}&sortBy=createdAt&sortOrder=desc`;
    console.log('API Service: Getting recent fund requests from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  async getPendingApprovals() {
    return this.request('/dashboard/pending-approvals');
  }

  // Projects
  async getProjects(params?: {
    page?: number;
    limit?: number;
    status?: string;
    type?: string;
    location?: string;
    assignedTo?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }
    
    // Make request without authentication token
    const url = `${API_BASE_URL}/projects?${searchParams.toString()}`;
    console.log('API Service: Getting projects from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  async getAssignedProjects() {
    return this.request('/projects/assigned');
  }

  async checkCashierAssignmentStatus(cashierId: string) {
    return this.request(`/projects/cashier-assignment-status/${cashierId}`);
  }

  // Employee Loans
  async getEmployeeLoans(params?: {
    page?: number;
    limit?: number;
    status?: string;
    employeeId?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }
    
    return this.request(`/employee-loans?${searchParams.toString()}`);
  }

  async getEmployeeLoan(id: string) {
    return this.request(`/employee-loans/${id}`);
  }

  async createEmployeeLoan(loanData: any) {
    return this.request('/employee-loans', {
      method: 'POST',
      body: JSON.stringify(loanData),
    });
  }

  async updateEmployeeLoan(id: string, loanData: any) {
    return this.request(`/employee-loans/${id}`, {
      method: 'PUT',
      body: JSON.stringify(loanData),
    });
  }

  async deleteEmployeeLoan(id: string) {
    return this.request(`/employee-loans/${id}`, {
      method: 'DELETE',
    });
  }

  async approveEmployeeLoan(id: string, approvalData: any) {
    return this.request(`/employee-loans/${id}/approve`, {
      method: 'PUT',
      body: JSON.stringify(approvalData),
    });
  }

  async processLoanPayment(id: string, paymentData: any) {
    return this.request(`/employee-loans/${id}/process-payment`, {
      method: 'PUT',
      body: JSON.stringify(paymentData),
    });
  }

  async getProject(id: string) {
    console.log('API Service: Getting project with ID:', id);
    
    if (!id || id === 'undefined' || id === 'null') {
      throw new Error('Invalid project ID');
    }
    
    try {
      // Make request without authentication token
      const url = `${API_BASE_URL}/projects/${id}`;
      console.log('API Service: Making request to:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      console.log('API Service: Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('API Service: Project response:', data);
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to load project');
      }
      
      if (!data.data) {
        throw new Error('No project data received');
      }
      
      return data;
    } catch (error: any) {
      console.error('API Service: Error getting project:', error);
      
      // Handle specific error cases
      if (error.message?.includes('404')) {
        throw new Error('Project not found');
      }
      
      throw error;
    }
  }

  async createProject(projectData: any) {
    const url = `${API_BASE_URL}/projects`;
    console.log('API Service: Creating project:', projectData);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(projectData),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  async updateProject(id: string, projectData: any) {
    const url = `${API_BASE_URL}/projects/${id}`;
    console.log('API Service: Updating project:', projectData);
    
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(projectData),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  async deleteProject(id: string) {
    return this.request(`/projects/${id}`, {
      method: 'DELETE',
    });
  }

  async recalculateProjectIncome(id: string) {
    return this.request(`/projects/${id}/recalculate-income`, {
      method: 'POST',
    });
  }

  async checkUserAssignmentStatus(userId: string) {
    return this.request(`/projects/user-assignment-status/${userId}`);
  }

  async assignStaff(projectId: string, staffData: { staffId: string; role: string }) {
    return this.request(`/projects/${projectId}/assign-staff`, {
      method: 'POST',
      body: JSON.stringify(staffData),
    });
  }

  async approveStaffAssignment(projectId: string, staffId: string) {
    return this.request(`/projects/${projectId}/approve-staff/${staffId}`, {
      method: 'PUT',
    });
  }

  // Fund Requests
  async getFundRequests(params?: {
    page?: number;
    limit?: number;
    status?: string;
    projectId?: string;
    requesterId?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }
    
    const url = `${API_BASE_URL}/fund-requests?${searchParams.toString()}`;
    console.log('API Service: Getting fund requests from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  async getFundRequest(id: string) {
    return this.request(`/fund-requests/${id}`);
  }

  async createFundRequest(requestData: any) {
    return this.request('/fund-requests', {
      method: 'POST',
      body: JSON.stringify(requestData),
    });
  }

  async updateFundRequest(id: string, requestData: any) {
    const url = `${API_BASE_URL}/fund-requests/${id}`;
    console.log('API Service: Updating fund request:', requestData);
    
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  async approveFundRequest(id: string, approvalData: any) {
    return this.request(`/fund-requests/${id}/approve`, {
      method: 'PUT',
      body: JSON.stringify(approvalData),
    });
  }

  // Cashier-specific fund request methods
  async getPendingCashierRequests() {
    return this.request('/fund-requests/pending-cashier');
  }

  async processCashierPayment(id: string, data: any) {
    return this.request(`/fund-requests/${id}/process-cashier-payment`, { 
      method: 'PUT', 
      body: JSON.stringify(data) 
    });
  }

  // Users
  async getUsers(params?: {
    page?: number;
    limit?: number;
    role?: string;
    status?: string;
    search?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }
    
    const url = `${API_BASE_URL}/users?${searchParams.toString()}`;
    console.log('API Service: Getting users from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  async getUser(id: string) {
    return this.request(`/users/${id}`);
  }

  async createUser(userData: any) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async updateUser(id: string, userData: any) {
    return this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async updateUserStatus(id: string, status: string) {
    return this.request(`/users/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  // Notifications
  async getNotifications(params?: {
    page?: number;
    limit?: number;
    read?: boolean;
    userId?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }
    
    const response = await this.request(`/notifications?${searchParams.toString()}`);
    
    const notificationsData = response;
    
    // Get unread count
    try {
      const token = getAuthToken();
      const unreadResponse = await fetch(`${API_BASE_URL}/notifications/unread-count`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      
      if (unreadResponse.ok) {
        const unreadData = await unreadResponse.json();
        return {
          ...notificationsData,
          unreadCount: unreadData.count || 0
        };
      }
    } catch (error) {
      console.error('Error getting unread count:', error);
    }
    
    return {
      ...notificationsData,
      unreadCount: 0
    };
  }

  async markNotificationAsRead(id: string) {
    return this.request(`/notifications/${id}/read`, {
      method: 'PUT',
    });
  }

  async markAllNotificationsAsRead() {
    return this.request('/notifications/read-all', {
      method: 'PUT',
    });
  }

  async getUnreadNotificationsCount() {
    return this.request('/notifications/unread-count');
  }

  async createNotification(notificationData: any) {
    return this.request('/notifications', {
      method: 'POST',
      body: JSON.stringify(notificationData),
    });
  }

  // Reports
  async getReportsStats() {
    return this.request('/reports/stats');
  }

  async generateReport(reportData: {
    type: string;
    parameters: any;
    format: string;
  }) {
    return this.request('/reports/generate', {
      method: 'POST',
      body: JSON.stringify(reportData),
    });
  }

  async downloadReport(reportData: {
    type: string;
    parameters: any;
    format: 'excel' | 'pdf';
  }) {
    const response = await this.request(`/reports/download/${reportData.format}`, {
      method: 'POST',
      body: JSON.stringify(reportData),
    });
    
    // Handle file download
    if (response.success && response.data) {
      const blob = new Blob([response.data], { 
        type: reportData.format === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportData.type.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.${reportData.format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
    
    return response;
  }

  // Office Budget
  async getOfficeBudget() {
    return this.request('/office-budget/current');
  }

  async createOfficeBudget(budgetData: any) {
    return this.request('/office-budget', {
      method: 'POST',
      body: JSON.stringify(budgetData),
    });
  }

  async updateOfficeBudget(id: string, budgetData: any) {
    return this.request(`/office-budget/${id}`, {
      method: 'PUT',
      body: JSON.stringify(budgetData),
    });
  }

  // System Logs
  async getMeaningfulLogs(params?: {
    page?: number;
    limit?: number;
    userId?: string;
    action?: string;
    module?: string;
    success?: boolean;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }
    
    return this.request(`/logs/meaningful?${searchParams.toString()}`);
  }

  async getSystemLogs(params?: {
    page?: number;
    limit?: number;
    userId?: string;
    action?: string;
    module?: string;
    success?: boolean;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }
    
    return this.request(`/logs?${searchParams.toString()}`);
  }

  async getLogStats(params?: {
    dateFrom?: string;
    dateTo?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }
    
    return this.request(`/logs/stats?${searchParams.toString()}`);
  }

  async getUserLogs(userId: string, params?: {
    page?: number;
    limit?: number;
  }) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }
    
    return this.request(`/logs/user/${userId}?${searchParams.toString()}`);
  }

  async exportLogs(params?: {
    format?: string;
    dateFrom?: string;
    dateTo?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }
    
    return this.request(`/logs/export?${searchParams.toString()}`);
  }

  // Office Budget
  async getOfficeBudgetRequests(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }
    
    return this.request(`/office-budget?${searchParams.toString()}`);
  }

  async approveOfficeBudgetRequest(id: string, approvalData: any) {
    return this.request(`/office-budget/${id}/approve`, {
      method: 'PUT',
      body: JSON.stringify(approvalData),
    });
  }

  async processOfficeBudgetPayment(id: string, paymentData: any) {
    return this.request(`/office-budget/${id}/process-payment`, {
      method: 'PUT',
      body: JSON.stringify(paymentData),
    });
  }

  // Company Balance
  async getCompanyBalance() {
    return this.request('/company-balance');
  }

  async updateCompanyBalance(balanceData: any) {
    return this.request('/company-balance', {
      method: 'PUT',
      body: JSON.stringify(balanceData),
    });
  }

  async getBalanceHistory(params?: {
    page?: number;
    limit?: number;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    return this.request(`/company-balance/history?${queryParams.toString()}`);
  }

  // Accounting Entries
  async getAccountingEntries(params?: {
    page?: number;
    limit?: number;
    type?: string;
    category?: string;
    approved?: boolean;
    dateFrom?: string;
    dateTo?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }
    
    return this.request(`/accounting?${searchParams.toString()}`);
  }

  async getAccountingEntry(id: string) {
    return this.request(`/accounting/${id}`);
  }

  async createAccountingEntry(entryData: any) {
    return this.request('/accounting', {
      method: 'POST',
      body: JSON.stringify(entryData),
    });
  }

  async updateAccountingEntry(id: string, entryData: any) {
    return this.request(`/accounting/${id}`, {
      method: 'PUT',
      body: JSON.stringify(entryData),
    });
  }

  async deleteAccountingEntry(id: string) {
    return this.request(`/accounting/${id}`, {
      method: 'DELETE',
    });
  }

  async approveAccountingEntry(id: string, approvalData: any) {
    return this.request(`/accounting/${id}/approve`, {
      method: 'PUT',
      body: JSON.stringify(approvalData),
    });
  }

  // Expenses
  async getExpenses(params?: {
    page?: number;
    limit?: number;
    status?: string;
    projectId?: string;
    category?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }
    
    const url = `${API_BASE_URL}/expenses?${searchParams.toString()}`;
    console.log('API Service: Getting expenses from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  async createExpense(expenseData: any) {
    // Check if expenseData is FormData (for file uploads) or regular object
    if (expenseData instanceof FormData) {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/expenses`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: expenseData,
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      return response.json();
    } else {
      // For JSON data without files
      return this.request('/expenses', {
        method: 'POST',
        body: JSON.stringify(expenseData),
      });
    }
  }

  async getExpense(id: string) {
    if (!id || id === 'undefined' || id === 'null') {
      throw new Error('Invalid expense ID');
    }
    return this.request(`/expenses/${id}`);
  }

  async getPendingExpenses() {
    return this.request('/expenses/pending');
  }

  // File Upload
  async uploadFile(file: File, type: 'receipt' | 'document' | 'profile' | 'general') {
    const token = getAuthToken();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    const response = await fetch(`${API_BASE_URL}/upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    return handleResponse(response);
  }

  // File Preview
  async getFilePreview(filename: string, subDir = 'general') {
    return this.request(`/preview/${filename}?subDir=${subDir}`);
  }

  async getFileContent(filename: string, subDir = 'general') {
    return this.request(`/preview/${filename}/content?subDir=${subDir}`);
  }

  async getMultipleFilesPreview(params?: {
    subDir?: string;
    page?: number;
    limit?: number;
  }) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }
    
    return this.request(`/preview?${searchParams.toString()}`);
  }

  // Payment methods
  async getPayments(params?: any) {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    const response = await fetch(`${API_BASE_URL}/payments${queryString}`);
    if (!response.ok) {
      throw new Error('Failed to fetch payments');
    }
    return response.json();
  }

  async getPayment(id: string) {
    if (!id || id === 'undefined' || id === 'null') {
      throw new Error('Invalid payment ID');
    }
    const response = await fetch(`${API_BASE_URL}/payments/${id}`);
    if (!response.ok) {
      throw new Error('Failed to fetch payment');
    }
    return response.json();
  }

  async createPayment(paymentData: any) {
    const url = `${API_BASE_URL}/payments`;
    const formData = new FormData();
    Object.keys(paymentData).forEach(key => {
      if (key !== 'attachments') { formData.append(key, paymentData[key]); }
    });
    if (paymentData.attachments && paymentData.attachments.length > 0) {
      paymentData.attachments.forEach((file: File) => { formData.append('attachments', file); });
    }
    const response = await fetch(url, { method: 'POST', body: formData });
    if (!response.ok) { throw new Error('Failed to create payment'); }
    return response.json();
  }

  async updatePayment(id: string, paymentData: any) {
    const url = `${API_BASE_URL}/payments/${id}`;
    const formData = new FormData();
    Object.keys(paymentData).forEach(key => {
      if (key !== 'attachments') { formData.append(key, paymentData[key]); }
    });
    if (paymentData.attachments && paymentData.attachments.length > 0) {
      paymentData.attachments.forEach((file: File) => { formData.append('attachments', file); });
    }
    const response = await fetch(url, { method: 'PUT', body: formData });
    if (!response.ok) { throw new Error('Failed to update payment'); }
    return response.json();
  }

  async deletePayment(id: string) {
    const response = await fetch(`${API_BASE_URL}/payments/${id}`, { method: 'DELETE' });
    if (!response.ok) { throw new Error('Failed to delete payment'); }
    return response.json();
  }

  async approvePayment(id: string, approvalData: any) {
    const response = await fetch(`${API_BASE_URL}/payments/${id}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(approvalData)
    });
    if (!response.ok) { throw new Error('Failed to approve payment'); }
    return response.json();
  }

  // Project approval methods
  async approveProject(id: string, approvalData: any) {
    const response = await fetch(`${API_BASE_URL}/projects/${id}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(approvalData)
    });
    if (!response.ok) { throw new Error('Failed to approve project'); }
    return response.json();
  }

  // Expense approval methods
  async approveExpense(id: string, approvalData: any) {
    return this.request(`/expenses/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(approvalData),
    });
  }

  // Set password with activation token
  async setPassword(token: string, password: string) {
    const url = `${API_BASE_URL}/users/set-password/${token}`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Regenerate activation token
  async regenerateActivationToken(userId: string) {
    return this.request(`/users/${userId}/regenerate-activation`, {
      method: 'POST',
    });
  }
}

// Create and export a single instance
const apiService = new ApiService();
export default apiService; 